<template>
  <div class="flex justify-between items-center">
    <span class="text-xl font-semibold text-gray-900">{{ $gettext("Documents") }}</span>

    <button
      v-if="documents.length"
      @click="downloadAll"
      class="flex items-center text-indigo-600 hover:text-indigo-800 text-sm"
    >
      <ArchiveBoxArrowDownIcon class="h-5 w-5 mr-1" />
      <span>{{ $gettext("Télécharger tout") }}</span>
    </button>
  </div>

  <div v-for="(docs, category) in groupedDocuments" :key="category" class="mt-4">
    <div class="flex items-center space-x-3 pb-2">
      <component
        :is="iconForCategory(category)"
        class="h-6 w-6 text-indigo-500"
      />
      <span>{{ categoryLabels[category] || category }}</span>
    </div>

    <div v-for="doc in docs" :key="doc.id" class="ml-8 mb-2">
      <a
        :href="doc.attributes.file.url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <div class="text-sm font-medium text-gray-900 flex items-center">
          <ArrowDownOnSquareIcon class="h-5 w-5" />
          <span class="ml-2">{{ doc.attributes.file.name }}</span>
        </div>
      </a>
    </div>
  </div>

  <div v-if="documents.length === 0" class="text-center text-gray-500 text-sm py-6">
    {{ $gettext("Aucun document trouvé.") }}
  </div>
</template>

<script setup>
import { computed } from "vue"
import { PencilSquareIcon, DocumentIcon, ArrowDownOnSquareIcon, ArchiveBoxArrowDownIcon } from "@heroicons/vue/24/outline"
import { useGettext } from "vue3-gettext"
import JSZip from "jszip"
import { saveAs } from "file-saver"

const { $gettext } = useGettext()

const props = defineProps({
  documents: {
    type: Array,
    required: true,
    default: () => [],
  },
})

const groupedDocuments = computed(() => {
  return props.documents.reduce((acc, doc) => {
    const category = doc.attributes.categoryName || "otherDocuments"
    if (!acc[category]) acc[category] = []
    acc[category].push(doc)
    return acc
  }, {})
})

const iconForCategory = (category) => {
  switch (category) {
    case "signatoryDocuments":
      return PencilSquareIcon
    default:
      return DocumentIcon
  }
}

const categoryLabels = {
  signatoryDocuments: $gettext("Document d'identité du signataire à la mission"),
  legalRepresentatives: $gettext("Statuts coordonnés"),
  physicalPersonEngagement: $gettext("Disposition régissant le pouvoir d'engager la personne morale"),
  administrators: $gettext("Liste des administrateurs"),
  uboRegisterExtract: $gettext("Extrait du registre des bénéficiaires effectifs"),
  identityDocuments: $gettext("Document d'identité"),
  otherDocuments: $gettext("Autre document(s)"),
  saleListingAgreement: $gettext("Mission de vente"),
  rentalListingAgreement: $gettext("Mission de location"),
  signedPurchaseAgreement: $gettext("Compromis de vente signé"),
  signedPurchaseOffer: $gettext("Offre d'achat signée"),
  signedRentalListingAgreement: $gettext("Bail de location signé")
}

const downloadAll = async () => {
  const zip = new JSZip()

  for (const doc of props.documents) {
    try {
      const fileUrl = doc.attributes?.file?.url
      const fileName = doc.attributes?.file?.name

      if (!fileUrl || !fileName) {
        console.warn(`Document ${doc.id} missing file URL or name, skipping`)
        continue
      }

      const response = await fetch(fileUrl)
      const blob = await response.blob()
      zip.file(fileName, blob)
    } catch (err) {
      console.error(`Failed to fetch document:`, err)
    }
  }

  const content = await zip.generateAsync({ type: "blob" })
  saveAs(content, "documents.zip")
}
</script>
